import type { ReactElement, SyntheticEvent } from "react"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import ConditionalWrapper from "@onestore/hel/dist/helpers/ConditionalWrapper"
import _t from "@core/i18n"
import useBasketActions from "@gatsby-plugin-basket/hooks/useBasketActions"
import type { CloudBluePlan } from "@gatsby-plugin-definitions/fragments/CloudBluePlan"
import type { UdsButton } from "@gatsby-plugin-generic-page/fragments/udsButton"
import buildButtonChildren from "@gatsby-plugin-generic-page/helpers/buildButtonChildren"
import type { InjectIconFunction } from "@ionos-web-design-system/icon/types"
import { Button, ButtonIcon } from "@ionos-web-design-system/react"
import ProductPrice from "../ProductPrice"

type UdsBasketButtonProps = Pick<UdsButton, "concept" | "variant" | "size"> & {
  plan: CloudBluePlan[]
  text: string
  title?: string
  className?: string
  hidePrice?: boolean
  onClick?: (event: SyntheticEvent) => void
  buttonType?: UdsButton["buttonType"]
  icon?: InjectIconFunction | null
}

function UdsBasketButton({
  onClick,
  text,
  title,
  concept,
  variant,
  size,
  hidePrice,
  plan,
  className,
  buttonType,
  icon,
}: UdsBasketButtonProps): ReactElement<UdsBasketButtonProps> {
  const { addProductToBasket, isLoading, isDone, goToBasket } =
    useBasketActions(plan[0])

  const handleOnClick = (event: SyntheticEvent) => {
    event.preventDefault()

    if (onClick) {
      onClick(event)
    }

    addProductToBasket()
  }

  const isSolo = buttonType === "icon-solo" ? icon : null

  const doneText = isDone ? _t("preBasket.upsell.added") : text

  const iconNode = icon ? <Button.Icon icon={icon} /> : null

  const buttonChildren = buildButtonChildren(buttonType, iconNode, doneText)

  return (
    <ConditionalWrapper
      condition={!hidePrice}
      wrapper={(children) => (
        <div className="flex items-center justify-between">
          <PushOrg
            rightSpace={{ small: 1, medium: 1, large: 2 }}
            leftSpace={{ large: 0, medium: 0, small: 1 }}
            hasNoReset
          >
            <ProductPrice hasTextColor plan={plan} />
          </PushOrg>

          {children}
        </div>
      )}
    >
      {isSolo ? (
        <ButtonIcon
          icon={isSolo}
          concept={concept}
          variant={variant}
          size={size}
          iconTitle={isDone ? _t("button.genericPageSuccess") : title}
          loading={isLoading}
          onClick={isDone ? goToBasket : handleOnClick}
          className={className}
        />
      ) : (
        <Button
          concept={concept}
          variant={variant}
          size={size}
          title={isDone ? _t("button.genericPageSuccess") : title}
          loading={isLoading}
          onClick={isDone ? goToBasket : handleOnClick}
          className={className}
        >
          {buttonChildren}
        </Button>
      )}
    </ConditionalWrapper>
  )
}

export default UdsBasketButton
